import { isBrazilianUf, type BrazilianUf } from "@/lib/brazilianUf";

export function normalizeCep(value: string): string {
  return value.replace(/\D/g, "");
}

export function isValidCep(value: string): boolean {
  return normalizeCep(value).length === 8;
}

/** Format digits as `00000-000`. */
export function formatCep(value: string): string {
  const digits = normalizeCep(value).slice(0, 8);
  if (digits.length === 0) return "";
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

export function maskCepInput(raw: string): string {
  return formatCep(raw);
}

export type CepAddressLookup = {
  street: string;
  neighborhood: string;
  city: string;
  state: BrazilianUf | "";
  ibgeMunicipalityCode: string;
};

type ViaCepResponse = {
  erro?: boolean | string;
  logradouro?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  ibge?: string;
};

type BrasilApiCepResponse = {
  street?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
};

function toAddressLookup(address: {
  street?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  ibgeMunicipalityCode?: string;
}): CepAddressLookup | null {
  const street = address.street?.trim() ?? "";
  const neighborhood = address.neighborhood?.trim() ?? "";
  const city = address.city?.trim() ?? "";
  const state = address.state != null && isBrazilianUf(address.state) ? address.state : "";
  const ibgeMunicipalityCode = address.ibgeMunicipalityCode?.replace(/\D/g, "").slice(0, 7) ?? "";
  if (street === "" && neighborhood === "" && city === "" && state === "") return null;
  return { street, neighborhood, city, state, ibgeMunicipalityCode };
}

async function lookupViaCep(digits: string): Promise<CepAddressLookup | null> {
  const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
  if (!response.ok) return null;
  const data = (await response.json()) as ViaCepResponse;
  if (data.erro === true || data.erro === "true") return null;
  return toAddressLookup({
    street: data.logradouro,
    neighborhood: data.bairro,
    city: data.localidade,
    state: data.uf,
    ibgeMunicipalityCode: data.ibge,
  });
}

async function lookupBrasilApi(digits: string): Promise<CepAddressLookup | null> {
  const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${digits}`);
  if (!response.ok) return null;
  const data = (await response.json()) as BrasilApiCepResponse;
  return toAddressLookup({
    street: data.street,
    neighborhood: data.neighborhood,
    city: data.city,
    state: data.state,
  });
}

/** Resolve street, neighborhood, city, and UF from a Brazilian postal code. */
export async function lookupCepAddress(value: string): Promise<CepAddressLookup | null> {
  const digits = normalizeCep(value);
  if (!isValidCep(digits)) return null;
  try {
    const viaCep = await lookupViaCep(digits);
    if (viaCep) return viaCep;
  } catch {
    // Fall through to BrasilAPI.
  }
  try {
    return await lookupBrasilApi(digits);
  } catch {
    return null;
  }
}
